/**
 * GET — Getalbegrip rulebook.
 *
 * Sourced from:
 *  - DEFAULT_POLICY.notation (Getalbegrip in latexFractionModules,
 *    commaDecimalModules)
 *  - DEFAULT_POLICY.distractor.modulePatterns.Getalbegrip
 *  - DEFAULT_POLICY.explanation.moduleRuleRefRequired.Getalbegrip = true
 *  - docs/guides/MODULE_AUTHORING_GUIDE.md §1
 *  - governanceRulesData.RULEBOOK_FILES.get.keyRules
 *  - docs/audits/VISUAL_QUESTION_TYPE_MAPPING_AUDIT_2026.md §4
 *
 * HINT ORDERING — patterns are evaluated TOP-DOWN; first match wins.
 * Always place the most specific pattern BEFORE the generic family-prefix
 * pattern (e.g. `^GET-KOMMA-DEEL` before `^GET-KOMMA-`).
 */

import type { ModuleRulebook } from './types'

export const GET_RULEBOOK: ModuleRulebook = {
  moduleId: 'Getalbegrip',
  shortCode: 'GET',
  displayName: 'Getalbegrip',

  didactics: {
    principles: [
      'plaatswaarde',
      'representatie_breuk_komma_procent',
      'visuele_modellen',
      'getallenlijn',
    ],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt', 'Reken Zeker'],
    misconceptionDomain: 'get',
    coreSummary:
      'Begrip van getallenstructuur: plaatswaarde, breuken, kommagetallen, procenten en hun onderlinge relaties.',
  },

  notation: {
    requireLatexFractions: true,
    requireCommaDecimals: true,
    forbiddenPatterns: [
      {
        pattern: '\\d+\\.\\d+',
        label: 'Punt als decimaalteken (gebruik komma: 3,14)',
      },
    ],
  },

  content: {
    extraForbiddenVocabByTierBand: {
      basic: ['noemer', 'teller', 'decimaalgetal'],
    },
    distractorMisconceptionPattern:
      'plaatswaarde|komma|afrond|decimaal|breuk|noemer|teller|macht|wortel|procent|verhouding',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: [
      'plaatswaarde',
      'gelijknamig',
      'vereenvoudigen',
      'omrekenen',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'fractionStrip',
      'fraction',
      'numberline',
      'percentageBar',
      'placeValueBlocks',
      'squareGrid',
      'barModel',
      'mapScale',
      'thermometer',
      'equalSharing',
      'splitDiagram',
      'column',
      'ratio',
    ],
    familyVisualHints: [
      // ── BREUK — fractionStrip first, with specific overrides ─────────────
      {
        familyIdPattern: '^GET-BREUK-DEEL',
        preferredStimulus: 'barModel',
        preferredSupport: 'fractionStrip',
        note: 'Breuk-van-getal vraagt om strookmodel (whole → deel), niet om losse breuk-pizza.',
      },
      {
        familyIdPattern: '^GET-BREUK-GEMENGD',
        preferredStimulus: 'fractionStrip',
        preferredSupport: 'numberline',
        note: 'Gemengde breuken: fractionStrip toont 1¼ = 5/4, numberline plaatst het.',
      },
      {
        familyIdPattern: '^GET-BREUK-GETALLENLIJN',
        preferredStimulus: 'numberline',
        note: 'Family-doel is letterlijk een getallenlijn.',
      },
      {
        familyIdPattern: '^GET-BREUK-RASTER',
        preferredStimulus: 'squareGrid',
        note: 'Breuken op een raster — kwart, helft, derde op vierkante grid.',
      },
      {
        familyIdPattern: '^GET-BREUK-',
        preferredStimulus: 'fractionStrip',
        preferredSupport: 'fraction',
        note: 'Breuk-families krijgen fractionStrip als primaire visualisatie; fraction (pizza/bar) als support.',
      },

      // ── KOMMA — per-bewerking differentiatie ─────────────────────────────
      {
        familyIdPattern: '^GET-KOMMA-LEZEN|^GET-KOMMA-WAARDE',
        preferredStimulus: 'placeValueBlocks',
        preferredSupport: 'squareGrid',
        questionTypeExempt: ['number_sequence', 'ordering'],
        note: 'Kommagetallen lezen/plaatswaarde: blokjes per plek (tienden/honderdsten); squareGrid (10×10 honderdblok) als support voor tienden/honderdsten. Uitzondering: number_sequence binnen deze families gebruikt bij voorkeur numberLineLeaps (visualiseert de stap), en ordering mag numberline gebruiken (positie-op-lijn is de kern).',
      },
      {
        familyIdPattern: '^GET-KOMMA-VERGELIJK|^GET-KOMMA-GETALLENLIJN',
        preferredStimulus: 'numberline',
        note: 'Vergelijken en positie op getallenlijn — getallenlijn is canoniek.',
      },
      {
        familyIdPattern: '^GET-KOMMA-AFRONDEN',
        preferredStimulus: 'numberline',
        note: 'Afronden: "welke kant op?" wordt zichtbaar op getallenlijn met buurankers.',
      },
      {
        familyIdPattern: '^GET-KOMMA-PLUS|^GET-KOMMA-MIN',
        preferredStimulus: 'column',
        preferredSupport: 'numberline',
        questionTypeExempt: ['number_sequence'],
        note: 'Optellen/aftrekken met komma: kolomopzet houdt komma op één plek; getallenlijn als support voor handig rekenen. Number_sequence-varianten gebruiken bij voorkeur numberLineLeaps (toont de constante stap).',
      },
      {
        familyIdPattern: '^GET-KOMMA-KEER',
        preferredStimulus: 'column',
        preferredSupport: 'splitDiagram',
        note: 'Vermenigvuldigen met komma: kolomopzet of split-diagram, niet getallenlijn.',
      },
      {
        familyIdPattern: '^GET-KOMMA-DEEL',
        preferredStimulus: 'equalSharing',
        preferredSupport: 'barModel',
        note: 'Delen met komma: gelijk verdelen-visual maakt "elk krijgt 0,25" tastbaar.',
      },

      // ── GROOT (positiestelsel) ───────────────────────────────────────────
      {
        familyIdPattern: '^GET-GROOT-POS|^GET-GROOT-LEZEN',
        preferredStimulus: 'placeValueBlocks',
        questionTypeExempt: ['number_sequence', 'ordering'],
        note: 'Plaatswaarde-vragen met MAB-blokjes (D/H/T/E) verankeren positie in concrete blokjes. Sequence/ordering-subtypes mogen numberline gebruiken.',
      },
      {
        familyIdPattern: '^GET-GROOT-VERGELIJK',
        preferredStimulus: 'placeValueBlocks',
        preferredSupport: 'numberline',
        questionTypeExempt: ['number_sequence', 'ordering'],
        note: 'Vergelijken: plaats-voor-plaats blokjes, getallenlijn als positie-anker. Voor number_sequence: bij voorkeur numberLineLeaps (toont de stap). Ordering mag numberline.',
      },
      {
        familyIdPattern: '^GET-GROOT-AFRONDEN|^GET-GROOT-SCHATTEN|^GET-ROND-',
        preferredStimulus: 'numberline',
        preferredSupport: 'placeValueBlocks',
        note: 'Afronden/schatten: numberline toont ankerpunten en richting.',
      },

      // ── PROC (procenten) ─────────────────────────────────────────────────
      {
        familyIdPattern: '^GET-PROC-VAN',
        preferredStimulus: 'percentageBar',
        preferredSupport: 'barModel',
        note: 'Procent van/getal: percentageBar toont %; strookmodel toont deel-van-geheel.',
      },
      {
        familyIdPattern: '^GET-PROC-BTW',
        preferredStimulus: 'percentageBar',
        preferredSupport: 'barModel',
        note: 'BTW als opslag op prijs — percentageBar, niet getallenlijn.',
      },
      {
        familyIdPattern: '^GET-PROC-TOENAME|^GET-PROC-AFNAME',
        preferredStimulus: 'percentageBar',
        preferredSupport: 'barModel',
        note: 'Procentuele verandering: balk laat oud→nieuw zien; numberline is hier didactisch zwak.',
      },
      {
        familyIdPattern: '^GET-PROC-SAMENGESTELD',
        preferredStimulus: 'percentageBar',
        preferredSupport: 'numberline',
        note: 'Samengestelde rente: balk per periode; numberline als tijdsas.',
      },
      {
        familyIdPattern: '^GET-PROC-',
        preferredStimulus: 'percentageBar',
        autoExemptEmbedded: true,
        questionTypeExempt: [
          'comparison_judge',
          'ordering',
          'categorize',
          'true_false_series',
          'bar_model_part',
        ],
        note: 'Procenten via een procenten-balk visualiseren. autoExemptEmbedded: alle interactieve types met eigen visueel oppervlak worden automatisch ge-exempt — de expliciete lijst hieronder blijft voor leesbaarheid maar is nu redundant.',
      },

      // ── VERH (verhoudingen) ──────────────────────────────────────────────
      {
        familyIdPattern: '^GET-VERH-SCHAAL-KAART',
        preferredStimulus: 'mapScale',
        preferredSupport: 'ratio',
        note: 'Schaal op kaart: mapScale-visual koppelt cm op kaart aan werkelijke afstand.',
      },
      {
        familyIdPattern: '^GET-VERH-SCHAAL',
        preferredStimulus: 'ratio',
        preferredSupport: 'mapScale',
        note: 'Schaal in tekening/blueprint: verhoudingstabel als primary.',
      },
      {
        familyIdPattern: '^GET-VERH-VERDELEN',
        preferredStimulus: 'barModel',
        note: 'Verhoudings-verdelen: strookmodel verdeelt het geheel in proportionele delen.',
      },
      {
        familyIdPattern: '^GET-VERH-RECEPT',
        preferredStimulus: 'ratio',
        preferredSupport: 'barModel',
        note: 'Recept-opschalen: ratio-tabel toont verhouding ingredient × factor.',
      },
      {
        familyIdPattern: '^GET-VERH-BREUK-PROC-KOPPEL',
        preferredStimulus: 'percentageBar',
        preferredSupport: 'fractionStrip',
        note: 'Koppeling breuk↔procent↔kommagetal: balk + breuk-strook naast elkaar.',
      },

      // ── NEGATIEF ─────────────────────────────────────────────────────────
      {
        familyIdPattern: '^GET-NEGATIEF',
        preferredStimulus: 'numberline',
        preferredSupport: 'thermometer',
        stimulusAllowed: ['numberline', 'thermometer'],
        autoExemptEmbedded: true,
        questionTypeExempt: [
          'comparison_judge',
          'ordering',
          'number_sequence',
          'thermometer_slider',
        ],
        note: 'Negatieve getallen: numberline canoniek; thermometer voor concrete context. Beide stimuli zijn didactisch geldig — VISUAL_STIMULUS_INCONSISTENT_IN_FAMILY mag de mix niet flaggen. autoExemptEmbedded dekt alle eigen-renderer types.',
      },

      // ── MACHT / WORTEL ───────────────────────────────────────────────────
      {
        familyIdPattern: '^GET-WORTEL|^GET-MACHT',
        preferredStimulus: 'squareGrid',
        autoExemptEmbedded: true,
        questionTypeExempt: [
          'comparison_judge',
          'square_root_builder',
          'ordering',
        ],
        note: 'Kwadraten en wortels als oppervlakte op een vierkant raster. autoExemptEmbedded dekt alle types met eigen visueel oppervlak (comparison_judge, square_root_builder, ordering, …).',
      },

      // ── DEEL (kommagetallen delen) ──────────────────────────────────────
      {
        familyIdPattern: '^GET-DEEL-KOMMA',
        preferredStimulus: 'equalSharing',
        preferredSupport: 'numberline',
        note: 'Delen met komma: gelijk-verdelen visual als primary.',
      },

      // ── VERGELIJK (generic) ─────────────────────────────────────────────
      {
        familyIdPattern: '^GET-VERGELIJK',
        preferredStimulus: 'numberline',
        autoExemptEmbedded: true,
        questionTypeExempt: ['comparison_judge', 'ordering'],
        note: 'Algemeen vergelijken op de getallenlijn. autoExemptEmbedded: alle types met eigen visueel oppervlak (comparison_judge, ordering, …) worden automatisch ge-exempt.',
      },

      // ── WETENSCHAPPELIJK ────────────────────────────────────────────────
      {
        familyIdPattern: '^GET-WETENSCHAP',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Wetenschappelijke notatie: geen vaste visual — regel-kaart met machten-van-10 als support. roleScope:rule-only onderdrukt VISUAL_SUPPORT_RULE_ONLY.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'comparison_judge',
      'estimation_slider',
      'number_sequence',
      'square_root_builder',
      'place_value_build',
      'bar_model_part',
    ],
    customTypeRoles: [
      {
        type: 'square_root_builder',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 9, max: 14 },
        reason:
          'Bouw een vierkant met N×N hokjes — verbindt kwadraat en wortel als oppervlakte-zijde-relatie.',
      },
      {
        type: 'place_value_build',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 1, max: 8 },
        reason:
          'Componeer een getal met MAB-blokjes per plaatswaarde — actief plaatswaarde-begrip i.p.v. MC-keuze.',
      },
      {
        type: 'bar_model_part',
        didacticRole: 'strategic',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 14 },
        reason:
          'Lees een deel af op het strookmodel — schoolboek-anker voor breuk/procent/verhouding van een geheel.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^GET-BREUK-(BASIS|LEZEN|RASTER|GETALLENLIJN)',
        preferredTypes: ['multiple_choice', 'fill_in'],
        discouragedTypes: ['estimation_slider'],
        note: 'Breuk-introductie: visueel afleiden van fractie/raster — exacte tellers/noemers vereist, geen slider-schatting.',
      },
      {
        familyIdPattern:
          '^GET-BREUK-(GEL-PLUS|GEL-MIN|ONG-PLUS|MIN|KEER|DEEL|UITBREIDEN|VEREENVOUDIG|GEMENGD)',
        preferredTypes: ['fill_in', 'multiple_choice', 'bar_model_part'],
        discouragedTypes: ['true_false_series', 'estimation_slider'],
        note: 'Breuk-bewerkingen vereisen exact resultaat (teller/noemer) — geen waar/onwaar-oordeel of slider.',
      },
      {
        familyIdPattern: '^GET-GROOT-(POS|LEZEN|VERGELIJK|AFRONDEN|SCHATTEN)',
        preferredTypes: [
          'place_value_build',
          'fill_in',
          'multiple_choice',
          'comparison_judge',
        ],
        discouragedTypes: ['estimation_slider'],
        note: 'Grote getallen: plaatswaarde actief componeren of vergelijken — slider verbergt cijfer-positie.',
      },
      {
        familyIdPattern: '^GET-KOMMA-(LEZEN|WAARDE|VERGELIJK|AFRONDEN)',
        preferredTypes: [
          'place_value_build',
          'fill_in',
          'multiple_choice',
          'comparison_judge',
        ],
        discouragedTypes: ['estimation_slider'],
        note: 'Kommagetal-begrip: plaatswaarde-blokjes of exacte invoer — niet schatten.',
      },
      {
        familyIdPattern: '^GET-KOMMA-(PLUS|MIN|KEER|DEEL)',
        preferredTypes: ['fill_in', 'multiple_choice'],
        discouragedTypes: ['estimation_slider', 'true_false_series'],
        note: 'Kommabewerkingen vereisen exact antwoord met juiste komma-positie.',
      },
      {
        familyIdPattern:
          '^GET-PROC-(BASIS|VAN|NIEUW|TERUG|TOENAME|AFNAME|BTW|SAMENGESTELD)',
        preferredTypes: ['fill_in', 'multiple_choice', 'bar_model_part'],
        discouragedTypes: ['true_false_series'],
        note: 'Procent van/tot geheel: strookmodel of exacte berekening — geen oordeels-vraagtype.',
      },
      {
        familyIdPattern: '^GET-(MACHT|WORTEL)',
        preferredTypes: ['square_root_builder', 'fill_in', 'multiple_choice'],
        discouragedTypes: ['estimation_slider', 'true_false_series'],
        note: 'Macht/wortel: square_root_builder maakt N×N-oppervlakte concreet — geen schatting.',
      },
      {
        familyIdPattern: '^GET-NEGATIEF',
        preferredTypes: [
          'number_sequence',
          'fill_in',
          'multiple_choice',
          'comparison_judge',
        ],
        note: 'Negatieve getallen: positie op getallenlijn of vergelijking — sequence/judge maken volgorde expliciet.',
      },
      {
        familyIdPattern: '^GET-VERH-',
        preferredTypes: ['fill_in', 'multiple_choice', 'bar_model_part'],
        discouragedTypes: ['true_false_series'],
        note: 'Verhoudingen: tabel of strookmodel — exact uitrekenen, geen oordeel.',
      },
      {
        familyIdPattern: '^GET-VERGELIJK',
        preferredTypes: ['comparison_judge', 'multiple_choice'],
        note: 'Vergelijken: comparison_judge dwingt kleiner/groter/gelijk keuze i.p.v. tussenstap.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'place-value-swap',
      label: 'Plaatswaarde verwisselen',
      description:
        'Cijfers in de verkeerde plaatswaarde lezen (b.v. tientallen als eenheden).',
    },
    {
      id: 'fraction-denominator-add',
      label: 'Noemers optellen',
      description:
        'Bij optellen van breuken zowel teller als noemer optellen i.p.v. gelijknamig maken.',
    },
    {
      id: 'comma-shift',
      label: 'Komma één plaats verschoven',
      description:
        'Bij omrekenen of vergelijken de komma een plaats te ver schuiven.',
    },
  ],

  keyRules: [
    'LaTeX-notatie verplicht voor breuken: $\\frac{1}{2}$',
    'Komma als decimaalteken (niet punt): 3,14 niet 3.14',
    'Visuele hulpmiddelen verplicht voor breuk-, komma- en procentfamilies',
    'Machten en wortels in LaTeX: $2^3$, $\\sqrt{9}$ — bij voorkeur met squareGrid-visual',
    'Plaatswaarde-uitleg expliciet benoemen in feedback; placeValueBlocks bij grote-getal-families',
    'Strookmodel (barModel) bij deel-van-geheel: procent, breuk, verhouding',
    'numberline NIET als default-visual gebruiken — alleen waar didactisch passend (positie, vergelijken, afronden, negatief)',
    'Trap-distractors: plaatswaarde verwisselen, noemers optellen, komma verschoven',
  ],

  authoringGuideAnchor: '#1-get--getalbegrip',
}
